import { supabase } from '@/integrations/supabase/client';

// ENT-2459 (follow-up to ENT-2169): a TOTP factor id is a durable identifier, so
// the sign-in flow lists factors once and caches the id while the user types
// their code. If that factor is revoked/re-enrolled inside the window (an admin
// reset, a re-enrolment in another tab), the cached id is dead: `challenge`
// rejects it and every retry re-submits the SAME dead id, so the user loops on
// "Invalid verification code" that no code can fix.
//
// Refresh the id ONCE on a definitive factor-not-found and retry. Deliberately
// narrow: a wrong code, a transient fault, or a genuinely absent factor must not
// be retried or masked.

type MfaErrorLike = {
  status?: unknown;
  code?: unknown;
  message?: unknown;
};

// A browser fetch can remain pending across a captive portal, dropped network
// response, or an interrupted connection. Supabase's MFA client API exposes no
// cancellation signal, so bound the UI-owned await rather than allowing a
// permanently disabled submit state. The outstanding request is still observed
// to prevent an unhandled late rejection. If it eventually succeeds, its
// confirmed MFA result is reconciled with the caller so the form cannot remain
// out of sync with an already AAL2 session.
export const MFA_VERIFICATION_TIMEOUT_MS = 15_000;

export class MfaVerificationTimeoutError extends Error {
  readonly code = 'mfa_verification_timeout';

  constructor() {
    super('Verification timed out. Check your connection and try again.');
    this.name = 'MfaVerificationTimeoutError';
  }
}

function withMfaVerificationTimeout<T>(
  operation: Promise<T>,
  onLateSuccess?: (value: T) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      reject(new MfaVerificationTimeoutError());
    }, MFA_VERIFICATION_TIMEOUT_MS);

    void operation.then(
      (value) => {
        window.clearTimeout(timeout);
        if (timedOut) {
          onLateSuccess?.(value);
          return;
        }
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

/**
 * True only for "the factor you named is not there".
 *
 * Matches MFA-specific signals ONLY — never a bare HTTP status. Supabase Auth
 * errors carry a machine-readable `code`, and the docs warn against relying on
 * status alone: a 403/404 can mean an unrelated Auth precondition (feature
 * disabled, AAL2 required, rate limit). Treating those as a missing factor
 * would tell the user their authenticator is no longer registered — the exact
 * wrong-cause mislabelling this module exists to remove. When no MFA signal is
 * present we fall through to the original error, keeping its real cause.
 *
 * A wrong code fails at `verify` with a 400/422 "Invalid TOTP code" and is never
 * matched here, so a bad code still fails on the first attempt with the normal
 * message.
 */
export function isFactorNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as MfaErrorLike;

  if (code === 'mfa_factor_not_found') return true;

  return typeof message === 'string' && /factor\s*not\s*found/i.test(message);
}

/** The user's current verified TOTP factor id, or null if there isn't one. */
export async function resolveVerifiedTotpFactorId(): Promise<string | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  // A failed probe is not evidence that the factor changed — report "unknown"
  // so the caller keeps the original error rather than inventing a retry.
  if (error) return null;

  return data?.totp?.find((factor) => factor.status === 'verified')?.id ?? null;
}

async function challengeAndVerifyOnce(factorId: string, code: string): Promise<void> {
  const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId,
  });
  if (challengeError) throw challengeError;

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challengeData.id,
    code,
  });
  if (verifyError) throw verifyError;
}

export interface ChallengeAndVerifyResult {
  /** The factor the code was ultimately verified against. */
  factorId: string;
  /** True when the cached id was stale and a refreshed one was used. */
  refreshed: boolean;
}

export interface ChallengeAndVerifyOptions {
  factorId: string;
  code: string;
  /**
   * Receives a confirmed result that arrived after the UI deadline. The
   * timeout remains visible immediately, but the caller can complete the
   * already-authenticated flow instead of leaving the form out of sync.
   */
  onLateSuccess?: (result: ChallengeAndVerifyResult) => void;
}

/**
 * Challenge + verify `code` against `factorId`, refreshing the id once if the
 * cached one turns out to be stale. Throws the underlying error on failure.
 */
export async function challengeAndVerifyWithFactorRefresh({
  factorId,
  code,
  onLateSuccess,
}: ChallengeAndVerifyOptions): Promise<ChallengeAndVerifyResult> {
  return withMfaVerificationTimeout((async () => {
    try {
      await challengeAndVerifyOnce(factorId, code);
      return { factorId, refreshed: false };
    } catch (error) {
      if (!isFactorNotFoundError(error)) throw error;

      // Only a *different* verified factor is worth retrying. If the id is
      // unchanged, or there is no verified factor at all, a retry would just
      // re-submit the same dead id — surface the original error instead.
      const refreshedFactorId = await resolveVerifiedTotpFactorId();
      if (!refreshedFactorId || refreshedFactorId === factorId) throw error;

      await challengeAndVerifyOnce(refreshedFactorId, code);
      return { factorId: refreshedFactorId, refreshed: true };
    }
  })(), onLateSuccess);
}
